import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { productsAPI, bannersAPI } from '../utils/api';
import ProductCard from '../components/ProductCard';
import SEO, { SITE_URL } from '../components/SEO';
import { ChevronRight } from 'lucide-react';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [saleProducts, setSaleProducts] = useState([]);
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [allProducts, featured, best, sale, bannersData] = await Promise.all([
          productsAPI.getAll({}),
          productsAPI.getAll({ is_featured: true }),
          productsAPI.getAll({ is_bestseller: true }),
          productsAPI.getAll({ min_price: 0 }),
          bannersAPI.getAll(true),
        ]);
        // Featured: use flagged products if available, otherwise latest products
        const featuredData = featured.data.length >= 4 ? featured.data.slice(0, 4) : allProducts.data.slice(0, 4);
        setFeaturedProducts(featuredData);
        // Bestsellers: use flagged products if available, otherwise latest products
        const bestData = best.data.length >= 4 ? best.data.slice(0, 4) : allProducts.data.slice(0, 4);
        setBestsellers(bestData);
        setSaleProducts(sale.data.filter(p => p.discount_price && p.discount_price < p.price).slice(0, 4));
        setBanners(bannersData.data);
      } catch (_) {
        // Data load failed silently
      }
    };
    loadData();
  }, []);

  const categories = [
    { name: 'Saree', slug: 'saree', image: 'https://customer-assets.emergentagent.com/job_regal-collection-2/artifacts/5bl0peyb_1%20%284%29%20copy.jpg' },
    { name: 'Lehenga', slug: 'lehenga', image: 'https://customer-assets.emergentagent.com/job_regal-collection-2/artifacts/5spd8ly2_1%20%281%29%20copy.jpg' },
    { name: 'Pakistani Suit', slug: 'pakistani-suit', image: 'https://customer-assets.emergentagent.com/job_regal-collection-2/artifacts/bvnrprul_1%20%281%29%20copy%20%281%29.jpg' },
    { name: 'Indo-Western', slug: 'indo-western', image: 'https://customer-assets.emergentagent.com/job_regal-collection-2/artifacts/tvfukgb3_1%20%282%29%20copy.jpg' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO
        title="Premium Ethnic Wear - Sarees, Lehengas, Pakistani Suits"
        description="Shop premium ethnic wear at Studio Crochet by Ekta Singh. Handcrafted Sarees, Lehengas, Pakistani Suits, Indo-Western outfits. COD available. Free shipping across India."
        canonical={SITE_URL}
      />

      {/* Promotional Offer Banner — top of page, immediately visible */}
      <section className="mt-20 bg-gradient-to-r from-[#1A1A18] via-[#2A2520] to-[#1A1A18] py-4 md:py-5" data-testid="promo-banner">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-8 text-center md:text-left">
            <div className="flex items-center gap-3">
              <span className="text-2xl md:text-3xl">✨</span>
              <span className="font-cormorant text-xl md:text-2xl lg:text-3xl text-white">SPECIAL OFFER</span>
              <span className="text-2xl md:text-3xl">✨</span>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <p className="font-outfit text-sm md:text-base text-[#EBE4D5]">
                Shop for <span className="text-[#C29B57] font-semibold">₹15,000</span> or more &amp; get <span className="text-[#C29B57] font-semibold">₹1,500 OFF</span>
              </p>
              <div className="flex items-center gap-2">
                <span className="font-outfit text-xs text-[#8C8577]">Use Code:</span>
                <span className="bg-[#C29B57] text-white font-outfit font-bold text-sm md:text-base px-4 py-1.5 tracking-widest rounded" data-testid="promo-coupon-code">EV1500</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center" 
          style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/62cdf97b-2a96-4971-b0b6-e9fea3545049/images/3aad39b5816d005529793f8c54063e6fd7d8457a07645ece120e5a0940443c0c.png')` }}
        >
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="font-cormorant text-4xl sm:text-5xl lg:text-6xl leading-none mb-4" data-testid="hero-title">Studio Crochet</h1>
          <p className="font-outfit text-lg sm:text-xl mb-8 tracking-wider uppercase" data-testid="hero-subtitle">Premium Ethnic Wear</p>
          <Link 
            to="/shop" 
            className="inline-block bg-[#1A1A18] text-white hover:bg-[#C29B57] transition-colors duration-300 rounded-none px-8 py-4 font-outfit uppercase tracking-wider text-sm"
            data-testid="shop-now-button"
          >
            Shop Now
          </Link>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-[#FCFBF8]" data-testid="categories-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-cormorant text-2xl sm:text-3xl lg:text-4xl leading-tight text-center mb-12" data-testid="categories-heading">Shop By Category</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category) => (
              <Link 
                key={category.slug} 
                to={`/shop?category=${category.slug}`}
                className="group relative aspect-[3/4] overflow-hidden bg-[#F3EFE6]"
                data-testid={`category-${category.slug}`}
              >
                <img 
                  src={category.image} 
                  alt={category.name}
                  className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300 flex items-end justify-center pb-8">
                  <h3 className="font-cormorant text-2xl text-white">{category.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-16 md:py-24 lg:py-32 bg-[#F3EFE6]" data-testid="featured-section">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <h2 className="font-cormorant text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="featured-heading">Featured Collection</h2>
              <Link to="/shop" className="flex items-center gap-2 text-[#C29B57] hover:text-[#99733D] transition-colors font-outfit text-sm uppercase tracking-wider" data-testid="view-all-featured">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bestsellers */}
      {bestsellers.length > 0 && (
        <section className="py-16 md:py-24 lg:py-32 bg-[#FCFBF8]" data-testid="bestsellers-section">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <h2 className="font-cormorant text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="bestsellers-heading">Best Sellers</h2>
              <Link to="/bestseller" className="flex items-center gap-2 text-[#C29B57] hover:text-[#99733D] transition-colors font-outfit text-sm uppercase tracking-wider" data-testid="view-all-bestsellers">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {bestsellers.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sale Section */}
      {saleProducts.length > 0 && (
        <section className="py-16 md:py-24 lg:py-32 bg-[#EBE4D5]" data-testid="sale-section">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <h2 className="font-cormorant text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="sale-heading">Sale</h2>
              <Link to="/sale" className="flex items-center gap-2 text-[#C29B57] hover:text-[#99733D] transition-colors font-outfit text-sm uppercase tracking-wider" data-testid="view-all-sale">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {saleProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Instagram Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-[#FCFBF8]" data-testid="instagram-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-cormorant text-2xl sm:text-3xl lg:text-4xl leading-tight text-center mb-4" data-testid="instagram-heading">Follow Us on Instagram</h2>
          <p className="font-outfit text-center text-[#8C8577] mb-8">@studio.crochet.ektasingh</p>
          
          <div className="text-center mb-12">
            <a 
              href="https://www.instagram.com/studio.crochet.ektasingh" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#515BD4] text-white px-8 py-4 rounded-full font-outfit uppercase tracking-wider text-sm hover:scale-105 transition-transform duration-300"
              data-testid="instagram-follow-button"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              Follow @studio.crochet.ektasingh
            </a>
          </div>

          <div className="bg-[#F3EFE6] rounded-2xl p-8 text-center">
            <p className="font-outfit text-lg text-[#1A1A18] mb-4">
              See our latest collections, behind-the-scenes, and style inspiration on Instagram
            </p>
            <p className="font-outfit text-sm text-[#8C8577]">
              Tag us in your Studio Crochet looks for a chance to be featured!
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 lg:py-32 bg-[#1A1A18] text-[#FCFBF8]" data-testid="testimonials-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-cormorant text-2xl sm:text-3xl lg:text-4xl leading-tight text-center mb-12 text-[#C29B57]" data-testid="testimonials-heading">What Our Clients Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: 'Priya Sharma', text: 'The quality and craftsmanship are exceptional. Every piece is a work of art!' },
              { name: 'Anjali Verma', text: 'I always get compliments when I wear Studio Crochet. Truly premium!' },
              { name: 'Neha Kapoor', text: 'Beautiful designs and excellent customer service. Highly recommended!' },
            ].map((testimonial) => (
              <div key={testimonial.name} className="bg-[#4A463F]/20 p-8 rounded-lg" data-testid={`testimonial-${testimonial.name.split(' ')[0].toLowerCase()}`}>
                <p className="font-outfit text-base leading-relaxed mb-4 italic">&ldquo;{testimonial.text}&rdquo;</p>
                <p className="font-cormorant text-lg text-[#C29B57]">{testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;